import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ApiError, api } from '@/api/client'
import type { CurrentUser, Session } from '@/api/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null)
  const loading = ref(false)

  /** False until the first session probe finishes, so the router can wait instead of guessing. */
  const initialized = ref(false)

  const isAuthenticated = computed(() => user.value !== null)
  const isAdmin = computed(() =>
    (user.value?.roles ?? []).some((role) => role === 'Owner' || role === 'Admin'),
  )
  const isOwner = computed(() => (user.value?.roles ?? []).includes('Owner'))

  function apply(session: Session) {
    user.value = session.user
  }

  function clear() {
    user.value = null
  }

  /** Probes the session cookie. A 401 here is the normal "not signed in" answer, not an error. */
  async function restore(): Promise<void> {
    if (initialized.value) return
    loading.value = true
    try {
      apply(await api.get<Session>('/api/auth/session'))
    } catch (error) {
      clear()
      if (!(error instanceof ApiError) || !error.isUnauthorized) {
        console.warn('Session probe failed', error)
      }
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  async function login(email: string, password: string, rememberMe: boolean) {
    loading.value = true
    try {
      apply(await api.post<Session>('/api/auth/login', { email, password, rememberMe }))
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  async function logout() {
    try {
      await api.post('/api/auth/logout')
    } finally {
      clear()
    }
  }

  return {
    user,
    loading,
    initialized,
    isAuthenticated,
    isAdmin,
    isOwner,
    restore,
    login,
    logout,
  }
})
